export type AssistantPosition = "left" | "right";

export interface DatasetAssistantState {
  widthRatio: number;
  position: AssistantPosition;
  collapsed: boolean;
  v2SidebarOpen: boolean;
}

const STATE_STORAGE_KEY = "sc-dataset-assistant-state";

const DEFAULT_WIDTH_RATIO = 0.4;

const DEFAULT_STATE: DatasetAssistantState = {
  widthRatio: DEFAULT_WIDTH_RATIO,
  position: "right",
  collapsed: false,
  v2SidebarOpen: true,
};

function parseJson(value: string | null): unknown {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parsePosition(value: unknown): AssistantPosition | null {
  return value === "left" || value === "right" ? value : null;
}

function parseBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function normalizeWidthRatio(value: unknown): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0 || numericValue > 1) {
    return DEFAULT_WIDTH_RATIO;
  }
  return numericValue;
}

export function loadDatasetAssistantState(): DatasetAssistantState {
  if (typeof window === "undefined") {
    return DEFAULT_STATE;
  }

  const parsed = parseJson(localStorage.getItem(STATE_STORAGE_KEY));
  const objectValue = parsed && typeof parsed === "object" ? parsed : null;

  const positionFromState = parsePosition(
    objectValue && "position" in objectValue ? objectValue.position : null
  );
  const collapsedFromState = parseBoolean(
    objectValue && "collapsed" in objectValue ? objectValue.collapsed : null
  );
  const widthRatioFromState = normalizeWidthRatio(
    objectValue && "widthRatio" in objectValue ? objectValue.widthRatio : null
  );
  const v2SidebarOpenFromState = parseBoolean(
    objectValue && "v2SidebarOpen" in objectValue ? objectValue.v2SidebarOpen : null
  );

  return {
    widthRatio: objectValue ? widthRatioFromState : DEFAULT_STATE.widthRatio,
    position: positionFromState ?? DEFAULT_STATE.position,
    collapsed: collapsedFromState ?? DEFAULT_STATE.collapsed,
    v2SidebarOpen: v2SidebarOpenFromState ?? DEFAULT_STATE.v2SidebarOpen,
  };
}

export function updateDatasetAssistantState(
  patch: Partial<DatasetAssistantState>
): DatasetAssistantState {
  const current = loadDatasetAssistantState();
  const next: DatasetAssistantState = {
    widthRatio: normalizeWidthRatio(patch.widthRatio ?? current.widthRatio),
    position: parsePosition(patch.position) ?? current.position,
    collapsed: typeof patch.collapsed === "boolean" ? patch.collapsed : current.collapsed,
    v2SidebarOpen:
      typeof patch.v2SidebarOpen === "boolean" ? patch.v2SidebarOpen : current.v2SidebarOpen,
  };

  localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(next));
  return next;
}
